// Pure composition math. No React, no IO — unit-testable in isolation.
//
// A PerpLeg + PredictionLeg compose into a piecewise-linear payoff curve:
//   - Perp P&L is linear in P, floored at -margin on the liquidation side.
//   - Prediction P&L is a step function at `threshold`: one of two constant
//     values depending on whether YES resolves.
// The sum has a kink (discontinuity) at the threshold; preserving that kink
// in the sampled output is the whole point of `composePayoff`'s extra pair.

export type PerpSide = "long" | "short";
export type PredictionOutcome = "yes" | "no";
export type CouplingDirection = "above" | "below";

export type PerpLeg = {
  side: PerpSide;
  /** Size in base units (e.g. 2.0 for 2 BTC). */
  size: number;
  /** Entry price in USD. */
  entry: number;
  leverage: number;
  /** Liquidation price in USD. Supply via liqPrice(). */
  liquidation: number;
};

export type PredictionLeg = {
  outcome: PredictionOutcome;
  shares: number;
  /** Entry price per share in cents (0–100). */
  entryCents: number;
  /** Underlying price at which YES resolves. */
  threshold: number;
  /**
   * YES resolves when underlying is `above` or `below` the threshold.
   * Examples:
   *   "Will BTC close above $80k?" → direction: 'above', threshold: 80000
   *   "Will BTC fall below $70k?"  → direction: 'below', threshold: 70000
   */
  direction: CouplingDirection;
};

export type PayoffPoint = {
  price: number;
  perpPnl: number;
  predPnl: number;
  totalPnl: number;
};

export type PriceRange = {
  min: number;
  max: number;
  steps: number;
};

// ---------------------------------------------------------------------------
// Isolated-margin liquidation — zero maintenance margin (v1 simplification)
// ---------------------------------------------------------------------------

export function liqPrice(
  side: PerpSide,
  entry: number,
  leverage: number,
): number {
  if (leverage <= 0) return side === "long" ? 0 : Number.POSITIVE_INFINITY;
  return side === "long"
    ? entry * (1 - 1 / leverage)
    : entry * (1 + 1 / leverage);
}

export function perpMargin(
  size: number,
  entry: number,
  leverage: number,
): number {
  if (leverage <= 0) return size * entry;
  return (size * entry) / leverage;
}

export function perpNotional(size: number, entry: number): number {
  return size * entry;
}

// ---------------------------------------------------------------------------
// Leg valuations at a given underlying price
// ---------------------------------------------------------------------------

/** Perp P&L at a given underlying price, floored at −margin (liq loss). */
export function perpPnl(leg: PerpLeg, price: number): number {
  const margin = perpMargin(leg.size, leg.entry, leg.leverage);
  const raw =
    leg.side === "long"
      ? leg.size * (price - leg.entry)
      : leg.size * (leg.entry - price);
  return Math.max(raw, -margin);
}

/** True if the YES outcome resolves at this underlying price. */
export function resolvesYes(leg: PredictionLeg, price: number): boolean {
  return leg.direction === "above"
    ? price >= leg.threshold
    : price <= leg.threshold;
}

/**
 * Prediction P&L at a given underlying price.
 *
 * For outcome=YES, shares S, entry E (0..1):
 *   YES resolves  → +S × (1 − E)
 *   YES does not  → −S × E
 * For outcome=NO, inverted:
 *   NO resolves   → +S × (1 − E)
 *   NO does not   → −S × E
 */
export function predPnl(leg: PredictionLeg, price: number): number {
  const entry = leg.entryCents / 100;
  const yesWins = resolvesYes(leg, price);
  const bettorWon =
    (leg.outcome === "yes" && yesWins) || (leg.outcome === "no" && !yesWins);
  return bettorWon
    ? leg.shares * (1 - entry)
    : -leg.shares * entry;
}

// ---------------------------------------------------------------------------
// composePayoff
// ---------------------------------------------------------------------------

/**
 * Sample the composite payoff curve across a price range.
 *
 * Samples `steps` evenly-spaced points, then injects a pair straddling
 * `pred.threshold` so the kink renders as a near-vertical step. The chart
 * consumer should plot with `linear` interpolation so the kink survives.
 */
export function composePayoff(
  perp: PerpLeg,
  pred: PredictionLeg,
  priceRange: PriceRange,
): PayoffPoint[] {
  const { min, max } = priceRange;
  const steps = Math.max(2, priceRange.steps);
  const step = (max - min) / (steps - 1);

  const push = (price: number, acc: PayoffPoint[]) => {
    const pp = perpPnl(perp, price);
    const dp = predPnl(pred, price);
    acc.push({ price, perpPnl: pp, predPnl: dp, totalPnl: pp + dp });
  };

  const points: PayoffPoint[] = [];
  for (let i = 0; i < steps; i++) {
    push(min + i * step, points);
  }

  // Inject a pair straddling the threshold so linear interpolation renders
  // the prediction-leg step as a near-vertical segment rather than smoothing
  // it across the nearest sampled interval.
  if (pred.threshold > min && pred.threshold < max) {
    const eps = Math.max((max - min) * 1e-6, Math.abs(pred.threshold) * 1e-9, Number.EPSILON * 1e3);
    push(pred.threshold - eps, points);
    push(pred.threshold, points);
  }

  points.sort((a, b) => a.price - b.price);
  return points;
}

// ---------------------------------------------------------------------------
// Derived stats (for the math tooltip / panel computed fields)
// ---------------------------------------------------------------------------

export function maxLoss(points: ReadonlyArray<PayoffPoint>): number {
  let m = Number.POSITIVE_INFINITY;
  for (const p of points) if (p.totalPnl < m) m = p.totalPnl;
  return Number.isFinite(m) ? m : 0;
}

export function maxGain(points: ReadonlyArray<PayoffPoint>): number {
  let m = Number.NEGATIVE_INFINITY;
  for (const p of points) if (p.totalPnl > m) m = p.totalPnl;
  return Number.isFinite(m) ? m : 0;
}
